import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import {
  Shield,
  Database,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
  Play,
  FileText,
  Activity,
  HardDrive,
  Server,
  Zap
} from 'lucide-react';

export default function DisasterRecoveryPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [backupConfigs, setBackupConfigs] = useState([]);
  const [backupJobs, setBackupJobs] = useState([]);
  const [restorePoints, setRestorePoints] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [drTests, setDrTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [newConfig, setNewConfig] = useState({ config_name: '', backup_type: 'full', schedule_cron: '0 2 * * *', retention_days: 30 });
  const [newIncident, setNewIncident] = useState({ title: '', description: '', severity: 'medium', category: 'outage', affected_services: [] });
  const [newTest, setNewTest] = useState({ test_name: '', test_type: 'tabletop', scheduled_date: '' });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, configsRes, jobsRes, pointsRes, incidentsRes, procsRes, testsRes] = await Promise.all([
        api.get('/api/disaster-recovery/dashboard'),
        api.get('/api/disaster-recovery/backup-configs'),
        api.get('/api/disaster-recovery/backup-jobs'),
        api.get('/api/disaster-recovery/restore-points'),
        api.get('/api/disaster-recovery/incidents'),
        api.get('/api/disaster-recovery/procedures'),
        api.get('/api/disaster-recovery/tests')
      ]);
      setDashboardData(dashRes.data);
      setBackupConfigs(configsRes.data || []);
      setBackupJobs(jobsRes.data || []);
      setRestorePoints(pointsRes.data || []);
      setIncidents(incidentsRes.data || []);
      setProcedures(procsRes.data || []);
      setDrTests(testsRes.data || []);
    } catch (error) {
      console.error('Error fetching DR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async () => {
    try {
      await api.post('/api/disaster-recovery/backup-configs', newConfig);
      toast({ title: 'Backup configuration created' });
      setShowConfigDialog(false);
      setNewConfig({ config_name: '', backup_type: 'full', schedule_cron: '0 2 * * *', retention_days: 30 });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleTriggerBackup = async (configId) => {
    try {
      await api.post(`/api/disaster-recovery/backup-jobs/trigger?config_id=${configId}`);
      toast({ title: 'Backup triggered successfully' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateIncident = async () => {
    try {
      await api.post('/api/disaster-recovery/incidents', newIncident);
      toast({ title: 'Incident created' });
      setShowIncidentDialog(false);
      setNewIncident({ title: '', description: '', severity: 'medium', category: 'outage', affected_services: [] });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleScheduleTest = async () => {
    try {
      await api.post('/api/disaster-recovery/tests', newTest);
      toast({ title: 'DR test scheduled' });
      setShowTestDialog(false);
      setNewTest({ test_name: '', test_type: 'tabletop', scheduled_date: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'degraded': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'unhealthy': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'default';
      case 'running': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="disaster-recovery-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Disaster Recovery</h1>
          <p className="text-muted-foreground">Business continuity and backup management</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups">Backups</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">System Health</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="procedures" data-testid="tab-procedures">Procedures</TabsTrigger>
          <TabsTrigger value="tests" data-testid="tab-tests">DR Tests</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Status Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System Health</CardTitle>
                {getHealthStatusIcon(dashboardData?.system_health?.overall)}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">{dashboardData?.system_health?.overall || 'Unknown'}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.system_health?.stats?.healthy || 0} healthy, {dashboardData?.system_health?.stats?.degraded || 0} degraded
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Restore Points</CardTitle>
                <HardDrive className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.restore_points?.available || 0}</div>
                <p className="text-xs text-muted-foreground">Available points</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Open Incidents</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.incidents?.open || 0}</div>
                <p className="text-xs text-muted-foreground">Active incidents</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Procedures</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.procedures?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Recovery runbooks</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Backups & Upcoming Tests */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Recent Backups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_backups?.jobs?.slice(0, 5).map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{job.job_type}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant={getJobStatusColor(job.status)}>{job.status}</Badge>
                    </div>
                  ))}
                  {(!dashboardData?.recent_backups?.jobs || dashboardData.recent_backups.jobs.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No recent backups</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Upcoming DR Tests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.dr_tests?.items?.slice(0, 5).map((test) => (
                    <div key={test.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{test.test_name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{test.test_type}</p>
                      </div>
                      <span className="text-sm">{new Date(test.scheduled_date).toLocaleDateString()}</span>
                    </div>
                  ))}
                  {(!dashboardData?.dr_tests?.items || dashboardData.dr_tests.items.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No scheduled tests</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* System Health Overview */}
          <Card>
            <CardHeader>
              <CardTitle>System Components Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                {dashboardData?.system_health?.components?.slice(0, 8).map((component) => (
                  <div key={component.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    {getHealthStatusIcon(component.status)}
                    <div>
                      <p className="font-medium">{component.component_name}</p>
                      <p className="text-sm text-muted-foreground capitalize">{component.check_type}</p>
                    </div>
                  </div>
                ))}
                {(!dashboardData?.system_health?.components || dashboardData.system_health.components.length === 0) && (
                  <p className="col-span-4 text-center text-muted-foreground py-4">No health checks recorded</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="backups" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Backup Management</h2>
            <Button onClick={() => setShowConfigDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Configuration
            </Button>
          </div>

          {/* Backup Configurations */}
          <Card>
            <CardHeader>
              <CardTitle>Backup Configurations</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Retention</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backupConfigs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell className="font-medium">{config.config_name}</TableCell>
                      <TableCell className="capitalize">{config.backup_type}</TableCell>
                      <TableCell className="font-mono text-sm">{config.schedule_cron}</TableCell>
                      <TableCell>{config.retention_days} days</TableCell>
                      <TableCell>
                        <Badge variant={config.is_active ? 'default' : 'secondary'}>
                          {config.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => handleTriggerBackup(config.id)}>
                          <Play className="h-4 w-4 mr-1" />
                          Run Now
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {backupConfigs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No backup configurations. Create one to start automated backups.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Backup Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backupJobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="capitalize">{job.job_type}</TableCell>
                      <TableCell>
                        <Badge variant={getJobStatusColor(job.status)}>{job.status}</Badge>
                      </TableCell>
                      <TableCell>{job.started_at ? new Date(job.started_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>{job.size_bytes ? `${(job.size_bytes / 1024 / 1024).toFixed(2)} MB` : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {backupJobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No backup jobs yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="space-y-4">
          <h2 className="text-xl font-semibold">System Health Monitoring</h2>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Healthy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData?.system_health?.stats?.healthy || 0}</div>
                <p className="text-muted-foreground">Components operating normally</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Degraded
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData?.system_health?.stats?.degraded || 0}</div>
                <p className="text-muted-foreground">Components with issues</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  Unhealthy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData?.system_health?.stats?.unhealthy || 0}</div>
                <p className="text-muted-foreground">Components failing</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Components</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Component</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Response Time</TableHead>
                    <TableHead>Last Check</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData?.system_health?.components?.map((component) => (
                    <TableRow key={component.id}>
                      <TableCell className="font-medium">{component.component_name}</TableCell>
                      <TableCell className="capitalize">{component.check_type}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getHealthStatusIcon(component.status)}
                          <span className="capitalize">{component.status}</span>
                        </div>
                      </TableCell>
                      <TableCell>{component.response_time_ms ? `${component.response_time_ms}ms` : '-'}</TableCell>
                      <TableCell>{new Date(component.last_check_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incidents" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">System Incidents</h2>
            <Button onClick={() => setShowIncidentDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Report Incident
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((incident) => (
                  <TableRow key={incident.id}>
                    <TableCell className="font-mono">{incident.incident_number}</TableCell>
                    <TableCell className="font-medium">{incident.title}</TableCell>
                    <TableCell>
                      <Badge variant={getSeverityColor(incident.severity)}>{incident.severity}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{incident.category}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{incident.status}</Badge>
                    </TableCell>
                    <TableCell>{new Date(incident.started_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {incidents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No incidents recorded. System operating normally.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="procedures" className="space-y-4">
          <h2 className="text-xl font-semibold">Recovery Procedures</h2>

          <div className="grid gap-4 md:grid-cols-2">
            {procedures.map((proc) => (
              <Card key={proc.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{proc.procedure_name}</CardTitle>
                    <Badge variant="outline">{proc.procedure_code}</Badge>
                  </div>
                  <CardDescription className="capitalize">{proc.category}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{proc.description || 'No description'}</p>
                  <div className="flex justify-between text-sm">
                    <span>Est. Duration: {proc.estimated_duration_minutes || '?'} min</span>
                    {proc.last_tested_at && (
                      <span>Last tested: {new Date(proc.last_tested_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {procedures.length === 0 && (
              <Card className="col-span-2">
                <CardContent className="text-center py-8 text-muted-foreground">
                  No recovery procedures defined.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tests" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">DR Tests</h2>
            <Button onClick={() => setShowTestDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Test
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>RTO/RPO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drTests.map((test) => (
                  <TableRow key={test.id}>
                    <TableCell className="font-medium">{test.test_name}</TableCell>
                    <TableCell className="capitalize">{test.test_type}</TableCell>
                    <TableCell>{new Date(test.scheduled_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{test.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {test.result && (
                        <Badge variant={test.result === 'passed' ? 'default' : 'destructive'}>
                          {test.result}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {test.rto_actual_minutes && test.rpo_actual_minutes ? (
                        <span className="text-sm">
                          RTO: {test.rto_actual_minutes}m / RPO: {test.rpo_actual_minutes}m
                        </span>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {drTests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No DR tests scheduled.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Backup Configuration</DialogTitle>
            <DialogDescription>Configure automated backup settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration Name</Label>
              <Input
                placeholder="Daily Full Backup"
                value={newConfig.config_name}
                onChange={(e) => setNewConfig({ ...newConfig, config_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Backup Type</Label>
                <Select
                  value={newConfig.backup_type}
                  onValueChange={(value) => setNewConfig({ ...newConfig, backup_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full</SelectItem>
                    <SelectItem value="incremental">Incremental</SelectItem>
                    <SelectItem value="differential">Differential</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Retention (Days)</Label>
                <Input
                  type="number"
                  value={newConfig.retention_days}
                  onChange={(e) => setNewConfig({ ...newConfig, retention_days: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Schedule (Cron)</Label>
              <Input
                placeholder="0 2 * * *"
                value={newConfig.schedule_cron}
                onChange={(e) => setNewConfig({ ...newConfig, schedule_cron: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Example: 0 2 * * * = 2:00 AM daily</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateConfig}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Incident Dialog */}
      <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Incident</DialogTitle>
            <DialogDescription>Log a system incident for tracking</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Brief description"
                value={newIncident.title}
                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={newIncident.severity}
                  onValueChange={(value) => setNewIncident({ ...newIncident, severity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newIncident.category}
                  onValueChange={(value) => setNewIncident({ ...newIncident, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outage">Outage</SelectItem>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="data_loss">Data Loss</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Detailed description..."
                value={newIncident.description}
                onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncidentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateIncident}>Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule DR Test</DialogTitle>
            <DialogDescription>Plan a disaster recovery test</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Name</Label>
              <Input
                placeholder="Q1 Full Failover Test"
                value={newTest.test_name}
                onChange={(e) => setNewTest({ ...newTest, test_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Test Type</Label>
                <Select
                  value={newTest.test_type}
                  onValueChange={(value) => setNewTest({ ...newTest, test_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tabletop">Tabletop</SelectItem>
                    <SelectItem value="simulation">Simulation</SelectItem>
                    <SelectItem value="full_failover">Full Failover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <Input
                  type="date"
                  value={newTest.scheduled_date}
                  onChange={(e) => setNewTest({ ...newTest, scheduled_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleTest}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
